import React, { useEffect } from 'react';
import { Control, Controller, FieldValues, Path, useWatch, UseFormSetValue } from 'react-hook-form';
import { Settings, Clock, Target, Repeat, Eye, HelpCircle, AlertCircle } from 'lucide-react';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Toggle } from '../ui/Toggle';
import { languageOptions, isEnglishLanguage } from '@/constants/languageOptions';

const questionTypeOptions = [
  { label: 'Single Choice', value: 'SINGLE_CHOICE' },
  { label: 'Multiple Choice', value: 'MULTIPLE_CHOICE' },
  { label: 'True/False', value: 'TRUE_FALSE' },
  { label: 'Text Answer', value: 'TEXT_ANSWER' },
];

const translationModeOptions = [
  {
    label: '🔤 HYBRID (Recommended)',
    value: 'HYBRID',
    helperText: 'Translated term with English reference - e.g., "ஜாவாஸ்கிரிப்ட் (JavaScript)"'
  },
  {
    label: '🌍 PURE',
    value: 'PURE',
    helperText: '100% target language, no English - e.g., "ஜாவாஸ்கிரிப்ட்"'
  },
  {
    label: '🔡 TECHNICAL_ENGLISH',
    value: 'TECHNICAL_ENGLISH',
    helperText: 'Keep technical terms in English - e.g., "JavaScript என்பது..."'
  }
];

interface ExamConfigSectionProps<T extends FieldValues> {
  control: Control<T>;
  setValue: UseFormSetValue<T>;
  errors?: any;
}

export function ExamConfigSection<T extends FieldValues>({
  control,
  setValue,
  errors,
}: ExamConfigSectionProps<T>) {
  // Watch content language to control translation mode
  const contentLanguage = useWatch({
    control,
    name: 'content_language' as Path<T>,
    defaultValue: 'en-US',
  });

  // Watch difficulty percentages
  const easyPercentage = useWatch({
    control,
    name: 'exam_config.difficulty_distribution.easy_percentage' as Path<T>,
    defaultValue: 40,
  });

  const mediumPercentage = useWatch({
    control,
    name: 'exam_config.difficulty_distribution.medium_percentage' as Path<T>,
    defaultValue: 40,
  });

  const hardPercentage = useWatch({
    control,
    name: 'exam_config.difficulty_distribution.hard_percentage' as Path<T>,
    defaultValue: 20,
  });

  // Check if selected language is English
  const isEnglish = isEnglishLanguage(contentLanguage as string);

  // Auto-set translation mode to PURE and disable when English is selected
  useEffect(() => {
    if (isEnglish) {
      setValue('language_translation_mode' as Path<T>, 'PURE' as any);
    }
  }, [isEnglish, setValue]);

  // Auto-adjust percentages to ensure they sum to 100
  useEffect(() => {
    const total = Number(easyPercentage) + Number(mediumPercentage) + Number(hardPercentage);

    if (total !== 100) {
      // Auto-adjust the last changed value to make total 100
      const difference = 100 - total;
      const newHardPercentage = Math.max(0, Math.min(100, Number(hardPercentage) + difference));
      setValue('exam_config.difficulty_distribution.hard_percentage' as Path<T>, newHardPercentage as any);
    }
  }, [easyPercentage, mediumPercentage]);

  const totalPercentage = Number(easyPercentage) + Number(mediumPercentage) + Number(hardPercentage);
  const isValidDistribution = totalPercentage === 100;
  const isTotalExceeded = totalPercentage > 100;

  return (
    <div className="space-y-6">
      {/* Section Header */}
      <div className="flex items-center gap-3 pb-4 border-b border-gray-200">
        <div className="p-2 bg-purple-100 rounded-lg">
          <Settings className="w-5 h-5 text-purple-600" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Exam Configuration</h3>
          <p className="text-sm text-gray-500">Configure AI generation and exam settings</p>
        </div>
      </div>

      {/* Language and Question Types */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Select
          name={'content_language' as Path<T>}
          control={control}
          label="Content Language"
          options={languageOptions}
          required
          error={errors?.content_language?.message}
          placeholder="Select language..."
        />

        <div className="w-full">
          <Controller
            name={'exam_config.question_types' as Path<T>}
            control={control}
            rules={{
              validate: (value: any) => {
                if (!value || value.length === 0) {
                  return 'Please select at least one question type';
                }
                return true;
              }
            }}
            render={({ field, fieldState: { error } }) => (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Question Types
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <Select
                  name={'exam_config.question_types' as Path<T>}
                  control={control}
                  options={questionTypeOptions}
                  isMulti
                  required
                  error={error?.message}
                  placeholder="Select question types..."
                />
              </div>
            )}
          />
        </div>
      </div>

      {/* Translation Mode for Non-English Content */}
      <div className={`p-4 bg-gradient-to-r rounded-lg border transition-all ${
        isEnglish
          ? 'from-gray-50 to-gray-100 border-gray-300 opacity-60 cursor-not-allowed'
          : 'from-purple-50 to-blue-50 border-purple-200'
      }`}>
        <div className="mb-3">
          <h4 className="text-sm font-semibold mb-1 flex items-center gap-2">
            🌐 Technical Terms Translation Mode
            {isEnglish && (
              <span className="text-xs font-normal text-white bg-red-500 px-2 py-0.5 rounded">
                Disabled for English
              </span>
            )}
          </h4>
          <p className="text-xs">
            {isEnglish
              ? 'This setting is automatically set to PURE for English content since no translation is needed.'
              : 'Controls how technical terms are handled in non-English content. This setting only applies when content language is not English.'
            }
          </p>
        </div>
        <Select
          name={'language_translation_mode' as Path<T>}
          control={control}
          label="Translation Mode"
          options={translationModeOptions}
          required
          error={errors?.language_translation_mode?.message}
          placeholder="Select translation mode..."
          helperText={isEnglish
            ? 'Automatically set to PURE for English content'
            : 'Choose how technical terms should be displayed in non-English content'
          }
          isDisabled={isEnglish}
        />
        {!isEnglish && (
          <div className="mt-3 space-y-2">
            <div className="text-xs text-gray-600">
              <div className="font-medium text-gray-700 mb-1">Examples:</div>
              <div className="space-y-1 ml-2">
                <div>• <span className="font-medium">HYBRID:</span> "ஜாவாஸ்கிரிப்ட் (JavaScript)" - Best for learning</div>
                <div>• <span className="font-medium">PURE:</span> "ஜாவாஸ்கிரிப்ட்" - Fully localized</div>
                <div>• <span className="font-medium">TECHNICAL_ENGLISH:</span> "JavaScript என்பது..." - Industry standard</div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Basic Settings Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Input
          name={'exam_config.total_questions' as Path<T>}
          control={control}
          label="Total Questions"
          type="number"
          placeholder="30"
          required
          error={errors?.exam_config?.total_questions?.message}
          helperText="Number of questions to generate (10-100)"
          rules={{
            required: 'Total questions is required',
            min: { value: 10, message: 'Minimum 10 questions' },
            max: { value: 100, message: 'Maximum 100 questions' },
          }}
        />

        <Input
          name={'exam_config.time_limit_minutes' as Path<T>}
          control={control}
          label="Time Limit (minutes)"
          type="number"
          placeholder="60"
          error={errors?.exam_config?.time_limit_minutes?.message}
          helperText="Exam duration in minutes"
          rules={{
            min: { value: 5, message: 'Minimum 5 minutes' },
            max: { value: 300, message: 'Maximum 300 minutes' },
          }}
        />

        <Input
          name={'exam_config.passing_score' as Path<T>}
          control={control}
          label="Passing Score (%)"
          type="number"
          placeholder="70"
          required
          error={errors?.exam_config?.passing_score?.message}
          helperText="Minimum score to pass (50-100)"
          rules={{
            required: 'Passing score is required',
            min: { value: 50, message: 'Passing score must be at least 50%' },
            max: { value: 100, message: 'Maximum 100%' },
          }}
        />

        <Input
          name={'exam_config.max_attempts' as Path<T>}
          control={control}
          label="Max Attempts"
          type="number"
          placeholder="3"
          error={errors?.exam_config?.max_attempts?.message}
          helperText="Maximum retry attempts (leave empty for unlimited)"
          rules={{
            min: { value: 1, message: 'Minimum 1 attempt' },
          }}
        />
      </div>

      {/* Difficulty Distribution */}
      <div className="p-5 bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg border border-blue-100">
        <div className="flex items-start gap-3 mb-4">
          <Target className="w-5 h-5 text-blue-600 mt-0.5" />
          <div className="flex-1">
            <h4 className="text-base font-semibold text-gray-900 mb-1">
              Difficulty Distribution
            </h4>
            <p className="text-sm text-gray-600">
              Adjust the percentage of questions by difficulty level (must total 100%)
            </p>
          </div>
          {isTotalExceeded && (
            <span className="text-xs font-medium text-red-600 bg-red-100 px-2 py-1 rounded flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              Exceeds 100%: {totalPercentage}%
            </span>
          )}
          {!isValidDistribution && !isTotalExceeded && (
            <span className="text-xs font-medium text-orange-600 bg-orange-100 px-2 py-1 rounded">
              Total: {totalPercentage}%
            </span>
          )}
          {isValidDistribution && (
            <span className="text-xs font-medium text-green-600 bg-green-100 px-2 py-1 rounded">
              ✓ Valid
            </span>
          )}
        </div>

        <div className="space-y-4">
          {/* Easy Slider */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <span className="w-3 h-3 bg-emerald-500 rounded-full"></span>
                Easy Questions
              </label>
              <span className="text-sm font-semibold text-gray-900 min-w-[3rem] text-right">
                {easyPercentage}%
              </span>
            </div>
            <input
              type="range"
              min="0"
              max="100"
              value={easyPercentage}
              onChange={(e) =>
                setValue(
                  'exam_config.difficulty_distribution.easy_percentage' as Path<T>,
                  parseInt(e.target.value) as any
                )
              }
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider-emerald"
            />
          </div>

          {/* Medium Slider */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <span className="w-3 h-3 bg-amber-500 rounded-full"></span>
                Medium Questions
              </label>
              <span className="text-sm font-semibold text-gray-900 min-w-[3rem] text-right">
                {mediumPercentage}%
              </span>
            </div>
            <input
              type="range"
              min="0"
              max="100"
              value={mediumPercentage}
              onChange={(e) =>
                setValue(
                  'exam_config.difficulty_distribution.medium_percentage' as Path<T>,
                  parseInt(e.target.value) as any
                )
              }
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider-amber"
            />
          </div>

          {/* Hard Slider */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <span className="w-3 h-3 bg-red-500 rounded-full"></span>
                Hard Questions
              </label>
              <span className="text-sm font-semibold text-gray-900 min-w-[3rem] text-right">
                {hardPercentage}%
              </span>
            </div>
            <input
              type="range"
              min="0"
              max="100"
              value={hardPercentage}
              onChange={(e) =>
                setValue(
                  'exam_config.difficulty_distribution.hard_percentage' as Path<T>,
                  parseInt(e.target.value) as any
                )
              }
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider-red"
            />
          </div>
        </div>

        {!isValidDistribution && (
          <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-xs text-red-700 flex items-center gap-2">
              <HelpCircle className="w-4 h-4" />
              Percentages must total exactly 100%. Current total: {totalPercentage}%
            </p>
          </div>
        )}
      </div>

      {/* Exam Behavior Toggles */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Toggle
          name={'exam_config.shuffle_questions' as Path<T>}
          control={control}
          label="Shuffle Questions"
          helperText="Randomize question order for each attempt"
        />

        <Toggle
          name={'exam_config.shuffle_options' as Path<T>}
          control={control}
          label="Shuffle Options"
          helperText="Randomize answer options for MCQs"
        />

        <Toggle
          name={'exam_config.show_feedback' as Path<T>}
          control={control}
          label="Show Feedback"
          helperText="Display explanations after each question"
        />

        <Toggle
          name={'exam_config.show_correct_answers' as Path<T>}
          control={control}
          label="Show Correct Answers"
          helperText="Reveal correct answers after submission"
        />

        <Toggle
          name={'exam_config.allow_retry' as Path<T>}
          control={control}
          label="Allow Retry"
          helperText="Enable multiple attempts (respects max attempts)"
        />
      </div>

      {/* Info Box */}
      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex gap-3">
          <HelpCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="text-sm font-semibold text-blue-900 mb-1">AI Generation Info</h4>
            <p className="text-xs text-blue-800">
              The AI will generate questions based on the course content, following your configuration.
              Generation typically takes 30-90 seconds depending on complexity. You can review and edit
              questions before publishing the certification.
            </p>
          </div>
        </div>
      </div>

      {/* Custom CSS for sliders */}
      <style>{`
        .slider-emerald::-webkit-slider-thumb {
          appearance: none;
          width: 18px;
          height: 18px;
          background: #10b981;
          cursor: pointer;
          border-radius: 50%;
          border: 3px solid white;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        }
        .slider-amber::-webkit-slider-thumb {
          appearance: none;
          width: 18px;
          height: 18px;
          background: #f59e0b;
          cursor: pointer;
          border-radius: 50%;
          border: 3px solid white;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        }
        .slider-red::-webkit-slider-thumb {
          appearance: none;
          width: 18px;
          height: 18px;
          background: #ef4444;
          cursor: pointer;
          border-radius: 50%;
          border: 3px solid white;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        }
      `}</style>
    </div>
  );
}
